import { useEffect } from 'react';

export type TooltipContentProvider = () => React.ReactNode | null | undefined;

export type RegisterTooltipProvider = (
	fieldId: string,
	provider: TooltipContentProvider | null,
) => void;

interface UseRegisterTooltipProviderArgs {
	registerTooltipProvider?: RegisterTooltipProvider;
	fieldId?: string;
	provider?: TooltipContentProvider;
	enabled?: boolean; // allows conditional registration (e.g. missing data)
}

/**
 * Registers a tooltip provider for a specific field and ensures proper cleanup.
 *
 * This hook calls the provided `registerTooltipProvider` function to associate a tooltip provider
 * with a given `fieldId`. If `provider` is `null` or `undefined`, it effectively clears the provider.
 * The registration only occurs if `enabled` is `true`, and all required arguments are present.
 * On component unmount or when dependencies change, the provider is cleared to prevent stale content.
 *
 * @example
 *   useRegisterTooltipContentProvider({
 *     registerTooltipProvider,
 *     fieldId: data?.fieldId,
 *     provider: () => <Text>{reasoning}</Text>,
 *     enabled: !!data?.fieldId,
 *   });
 *
 * @param registerTooltipProvider - Function to register or clear a tooltip provider for a field.
 * @param fieldId - The unique identifier of the field for which the tooltip provider is registered.
 * @param provider - The tooltip provider instance or `null` to clear the provider.
 * @param enabled - Whether the registration should be active. Defaults to `true`.
 */
export function useRegisterTooltipContentProvider({
	registerTooltipProvider,
	fieldId,
	provider,
	enabled = true,
}: UseRegisterTooltipProviderArgs): void {
	useEffect(() => {
		if (!enabled || !registerTooltipProvider || !fieldId) {
			return;
		}

		// Register current provider (can be null which effectively clears it)
		registerTooltipProvider(fieldId, provider ?? null);

		// Cleanup always clears the provider to avoid stale content retention.
		return () => {
			registerTooltipProvider(fieldId, null);
		};
	}, [registerTooltipProvider, fieldId, provider, enabled]);
}
